import { SnapshotIn, SnapshotOut, types, type Instance } from 'mobx-state-tree'

import { ExistingChecklist, ExistingChecklistModel } from '../Checklists/ChecklistModel'
import { EmailAddressModel } from './EmailAddressModel'

import { SelectOption, selectOptionFromString } from 'components/Select/Select'
import { ChecklistKind } from '../Checklists/ChecklistKind'
import { CenterOpenState } from '../Navbar/CenterOpenState'


const CenterModelWithReferences = types
  .model('CenterModelWithReferences', {
    checklists: types.array(types.reference(ExistingChecklistModel)),
    email_addresses: types.array(EmailAddressModel),
    id: types.identifierNumber,
    name: types.string,
    open_state: types.enumeration('CenterState', Object.values(CenterOpenState)),
    timezone: types.string,
    // vehicles: types.array(VehicleModel), no need for center's vehicles anywhere in the center-app for now
  })
  .views((self) => ({
    get emailAddresses() {
      return self.email_addresses
    },
    get centerState() {
      return self.open_state
    },
  }))
  .views((self) => ({
    get timezoneAsSelectOption(): SelectOption {
      return selectOptionFromString(self.timezone)
    },
    get centerOpenChecklist(): ExistingChecklist | null {
      return self.checklists.find(checklist => checklist.checklistKind === ChecklistKind.CenterDayOpen) ?? null
    },
    get centerCloseChecklist(): ExistingChecklist | null {
      return self.checklists.find(checklist => checklist.checklistKind === ChecklistKind.CenterDayClose) ?? null
    }
  }))
  .actions((self) => ({
    setName(name: typeof self.name) {
      self.name = name
    },
    setCenterOpenChecklist(checklist: ExistingChecklist | null) {
      const currentCenterOpenChecklist = self.centerOpenChecklist
      if (checklist?.id  === currentCenterOpenChecklist?.id) {
        return
      }

      if (currentCenterOpenChecklist !== null) {
        self.checklists.remove(currentCenterOpenChecklist)
      }

      if (checklist !== null) {
        self.checklists.push(checklist)
      }
    },
    setCenterCloseChecklist(checklist: ExistingChecklist | null) {
      const currentCenterCloseChecklist = self.centerCloseChecklist
      if (checklist?.id  === currentCenterCloseChecklist?.id) {
        return
      }

      if (currentCenterCloseChecklist !== null) {
        self.checklists.remove(currentCenterCloseChecklist)
      }

      if (checklist !== null) {
        self.checklists.push(checklist)
      }
    }
  }))
  .actions((self) => ({
    setTimezone(timezone: typeof self.timezone): void {
      self.timezone = timezone
    },
    addEmailAddress(): void {
      const draftEmailAddress = EmailAddressModel.create({ email_address: '' })
      self.emailAddresses.push(draftEmailAddress)
    },
    removeEmailAddress(emailAddress: typeof self.emailAddresses[number]): void {
      self.emailAddresses.remove(emailAddress)
    },
  }))

// The backend currently sends the the full checklist objects, not checklist ids.
export interface CenterWithChecklistObjects extends Omit<SnapshotIn<typeof CenterModelWithReferences>, 'checklists'> {
  checklists: Array<{id: string}>
}

// We change the checklists field in the snapshot from explicit full objects to the id strings, 
// So that the references in CenterModelWithReferences will be reconciliated as references.
// When serializing, we insert an object with only the `id` field.
export const CenterModel = types.snapshotProcessor(CenterModelWithReferences, {
  preProcessor(snapshot: CenterWithChecklistObjects) {
    return {
        ...snapshot,
        checklists: snapshot.checklists.map(checklist => checklist.id),
    }
  },
  postProcessor(instanceSnapshot: SnapshotOut<typeof CenterModelWithReferences>) {
    return {
      ...instanceSnapshot,
      checklists: instanceSnapshot.checklists.map(checklistId => ({id: checklistId})),
    }
  }
})


export type Center = Instance<typeof CenterModel>
